import DefaultLayout from '../../components/DefaultLayout';
import { useIntl } from 'react-intl';
import {
  StyledCartPage,
  StyledNotification,
  StyledWholesalePaymentBlock,
} from './style';
import {
  CartActionType,
  FarmProduct,
  WholesaleDeliveryCondition,
  WholesaleDeliveryMode,
  useCartDispatch,
  useCartState,
} from 'public/context/CartContext';
import { ProductData } from 'types/ProductData';
import React, { useEffect, useState } from 'react';
import CartStep from 'enums/cart-step';
import { Button, Icon, SmartIdModal, Spinner } from 'ui';
import Cart from 'public/components/DefaultLayout/Cart';
import ProcessPurchase from 'public/components/DefaultLayout/ProcessPurchase';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StyledAntdCartSteps } from 'ui/Steps/style';
import { Divider, Form, message, notification, StepProps, Tooltip } from 'antd';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { createCartFingerprint } from '../../../utils';
import { useForm } from 'antd/es/form/Form';
import useMarketType from '../../../utils/useMarketType';
import { useCoupons } from '../../components/DefaultLayout/CartSummary/useCoupons';
import PublicRoute from 'enums/publicRoutes';
import WholesaleDelivery from 'public/components/DefaultLayout/WholesaleDelivery';

type BackButton = {
  text: string;
  onClick: () => void;
};

export const CartPage = () => {
  const [isOpenSmartId, setIsOpenSmartId] = useState(false);

  const intl = useIntl();
  const [form] = useForm();
  const [userForm] = Form.useForm();
  const {
    farmProducts,
    priceTotal,
    wholesaleDeliveryMode,
    wholesaleDeliveries,
  } = useCartState();
  const [current, setCurrent] = useState<CartStep>(CartStep.CART);
  const [pageTitle, setPageTitle] = useState<string>('');
  const [backButton, setBackButton] = useState<BackButton>({
    text: intl.formatMessage({ id: 'general.back_to_catalog' }),
    onClick: () => navigate(PublicRoute.PRODUCTS_ALL),
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fingerPrint = createCartFingerprint(farmProducts);
  const { marketType, isEmarket } = useMarketType();
  const { coupons } = useCoupons();
  const { requiresAuth, cartId } = useCartState();
  const dispatch = useCartDispatch();

  const [orderReceipt, setOrderReceipt] = useState<{
    farmProducts: FarmProduct[];
    priceTotal: number;
  } | null>(null);

  const [repeatOrderProcessed, setRepeatOrderProcessed] = useState(
    () => !localStorage.getItem('pendingRepeatOrder')
  );
  const [notificationApi, contextHolder] = notification.useNotification();

  const { appendData: cartPay, isLoading: isBuying } = useQueryApiClient({
    request: {
      url: `/api/v2/emarket/pay`,
      method: 'POST',
      headers: { 'X-Cart-Fingerprint': fingerPrint },
    },
    onSuccess: (response) => {
      const withoutUpdate = onCartUpdated(response);

      if (withoutUpdate && response) {
        window.location.href = response;
      }
    },
    onError: (response) => {
      console.log(response);

      if (response.code === 412) {
        message.error(intl.formatMessage({ id: response.error }));
        return;
      }

      message.error(intl.formatMessage({ id: 'message.payment_failed' }));
      navigate(
        (isEmarket ? PublicRoute.CART_PAGE : PublicRoute.WHOLESALE_CART) +
          (isEmarket ? '?PROCESS_ORDER=1' : '')
      );
    },
  });

  const { appendData } = useQueryApiClient({
    request: {
      url: `api/v2/products/cart/refetch`,
      method: 'POST',
    },
    onSuccess: (response) => {
      const withoutUpdate = onCartUpdated(response);

      if (withoutUpdate) {
        setCurrent((old) => old + 1);
      }
    },
  });

  const { appendData: fetchOrder, isLoading: orderLoading } = useQueryApiClient({
    request: {
      url: 'api/v2/farm/order/:id',
      disableOnMount: true,
    },
    onSuccess: (response) => {
      setOrderReceipt({
        farmProducts: response.farmProducts,
        priceTotal: response.priceTotal,
      });
    },
  });

  const onCartUpdated = (response: any) => {
    if (response?.cartUpdated) {
      notificationApi['warning']({
        message: intl.formatMessage({ id: 'cart.updated_message' }),
        description: intl.formatMessage({ id: 'cart.updated_description' }),
        showProgress: true,
        pauseOnHover: true,
        duration: 10,
      });
      setCurrent(0);
    }

    if (response?.marketType) {
      dispatch({
        type: CartActionType.REFETCH_CART_PRODUCTS,
        farmProducts: response.farmProducts,
        priceTotal: response.priceTotal,
        requiresAuth: response.requires_auth,
        cartId: response.cartId,
        cartName: response.cartName,
        marketType: response.marketType,
      });
    }

    return !response?.cartUpdated;
  };

  const handleBuyProducts = async (paymentMethod = 'PAY') => {
    if (!setCurrent) return;

    const shouldSubmit =
      (current !== undefined && current >= CartStep.PROCESS_ORDER) ||
      (!isEmarket && current === CartStep.CART);

    if (shouldSubmit) {
      try {
        await form.validateFields();
        const checkboxes = form?.getFieldsValue();

        if (requiresAuth) {
          setIsOpenSmartId(true);
          return;
        }

        cartPay({
          type: marketType,
          paymentMethod: paymentMethod,
          farmProducts: farmProducts,
          priceTotal: priceTotal,
          personalInfo: isEmarket ? userForm.getFieldsValue() : {},
          ...checkboxes,
          coupons: coupons,
          cartId: cartId,
          wholesaleDeliveryMode: wholesaleDeliveryMode,
          wholesaleDeliveries: wholesaleDeliveries,
        });

        return;
      } catch (e) {
        return;
      }
    } else {
      appendData({
        farmProducts,
        priceTotal,
        coupons,
        marketType,
        cartId,
      });
    }
  };

  useEffect(() => {
    switch (current) {
      case CartStep.CART:
        setPageTitle(intl.formatMessage({ id: 'cart.your_cart_title' }));
        setBackButton({
          text: intl.formatMessage({ id: 'general.back_to_catalog' }),
          onClick: () =>
            navigate(
              isEmarket
                ? PublicRoute.PRODUCTS_ALL
                : PublicRoute.WHOLESALE_PRODUCTS_ALL
            ),
        });
        break;
      case CartStep.PROCESS_ORDER:
        setPageTitle(intl.formatMessage({ id: 'cart.process_purchase' }));
        setBackButton({
          text: intl.formatMessage({ id: 'general.back_to_cart' }),
          onClick: () => setCurrent(CartStep.CART),
        });
        break;
      case CartStep.RECEIVE_ORDER:
        setPageTitle(
          intl.formatMessage({ id: 'cart.your_order_received_title' })
        );
        setBackButton({
          text: intl.formatMessage({ id: 'general.continue_shopping' }),
          onClick: () =>
            navigate(
              isEmarket
                ? PublicRoute.PRODUCTS_ALL
                : PublicRoute.WHOLESALE_PRODUCTS_ALL
            ),
        });
        break;
    }
  }, [current, intl]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const step = searchParams.get('PROCESS_ORDER');

    if (tab) {
      const numericTab = parseInt(tab, 10);
      setCurrent(numericTab as CartStep);
    }

    if (step && isEmarket) {
      setCurrent(CartStep.PROCESS_ORDER);
    }
  }, [searchParams]);

  useEffect(() => {
    const orderId = searchParams.get('order_id');

    if (
      isEmarket ||
      searchParams.get('status') !== 'success' ||
      !orderId ||
      orderReceipt
    ) {
      return;
    }

    setCurrent(CartStep.RECEIVE_ORDER);
    dispatch({ type: CartActionType.CLEAR_CART });
    fetchOrder({}, { id: orderId });
  }, [isEmarket, searchParams, orderReceipt]);

  useEffect(() => {
    const pending = localStorage.getItem('pendingRepeatOrder');
    if (!pending) return;

    localStorage.removeItem('pendingRepeatOrder');

    const data = JSON.parse(pending) as {
      products: { product: ProductData; amount: number }[];
      wholesaleDeliveryMode: WholesaleDeliveryMode;
      wholesaleDeliveries: WholesaleDeliveryCondition[];
    };

    data.products.forEach(({ product, amount }) => {
      dispatch({
        type: CartActionType.ADD_ITEM,
        product,
        amount,
      });
    });

    if (data.wholesaleDeliveryMode && data.wholesaleDeliveries?.length) {
      dispatch({
        type: CartActionType.SET_WHOLESALE_DELIVERY_MODE,
        mode: data.wholesaleDeliveryMode,
      });
      dispatch({
        type: CartActionType.SET_WHOLESALE_DELIVERIES,
        deliveries: data.wholesaleDeliveries,
      });
    }

    setRepeatOrderProcessed(true);
  }, []);

  const baseSteps: StepProps[] = isEmarket
    ? [
        {
          style: { display: 'flex', justifyContent: 'flex-start' },
          title: intl.formatMessage({ id: 'general.basket' }),
        },
        {
          style: { display: 'flex', justifyContent: 'center' },
          title: intl.formatMessage({ id: 'general.process_your_purchase' }),
        },
        {
          style: { display: 'flex', justifyContent: 'flex-end' },
          title: intl.formatMessage({ id: 'general.order_receipt' }),
        },
      ]
    : [
        {
          style: { display: 'flex', justifyContent: 'flex-start' },
          title: intl.formatMessage({ id: 'general.basket' }),
        },
        {
          style: { display: 'flex', justifyContent: 'flex-end' },
          title: intl.formatMessage({ id: 'general.order_receipt' }),
        },
      ];

  const wholesaleStepMap: Record<number, number> = {
    [CartStep.CART]: 0,
    [CartStep.RECEIVE_ORDER]: 1,
  };

  const displayCurrent = isEmarket ? current : (wholesaleStepMap[current] ?? 0);

  const stepsItems = baseSteps.map((step, index) => {
    const item: StepProps = { ...step };

    if (index === displayCurrent) {
      item.status = 'process';
    } else {
      item.status = 'wait';
    }

    return item;
  });

  const steps = (
    <StyledAntdCartSteps
      type="navigation"
      current={displayCurrent}
      items={stepsItems}
    />
  );

  return (
    <DefaultLayout.PageLayout>
      <DefaultLayout.PageContent>
        <StyledCartPage>
          {contextHolder}
          <div className="back-button" onClick={backButton.onClick}>
            <Icon name={'arrow-left'} type={'any'} />
            <span>{backButton.text}</span>
          </div>
          <div className="container">
            <div className={'cart-page-title'}>
              <h1>{pageTitle}</h1>
            </div>
            {steps}
            {isEmarket ? (
              <Divider />
            ) : (
              <>
                {farmProducts?.length > 1 && (
                  <StyledNotification>
                    {intl.formatMessage({ id: 'cart_summary.notification' })}
                  </StyledNotification>
                )}
              </>
            )}
            {current === CartStep.CART && (
              repeatOrderProcessed ? (
                <Cart
                  priceTotal={priceTotal}
                  farmProducts={farmProducts}
                  setCurrent={setCurrent}
                  current={current}
                  isView={false}
                  form={!isEmarket ? form : undefined}
                />
              ) : (
                <Spinner spinning={true}><div /></Spinner>
              )
            )}
            {current === CartStep.PROCESS_ORDER && isEmarket && (
              <ProcessPurchase
                priceTotal={priceTotal}
                setCurrent={setCurrent}
                farmProducts={farmProducts}
                current={current}
                isView={false}
                form={form}
                isBuying={isBuying}
                userForm={userForm}
                handleBuyProducts={handleBuyProducts}
              />
            )}
            {current === CartStep.RECEIVE_ORDER && (
              repeatOrderProcessed && (isEmarket || orderReceipt) ? (
                <Cart
                  priceTotal={orderReceipt ? orderReceipt.priceTotal : priceTotal}
                  farmProducts={
                    orderReceipt ? orderReceipt.farmProducts : farmProducts
                  }
                  setCurrent={setCurrent}
                  current={current}
                  isView={true}
                />
              ) : (
                <Spinner spinning={orderLoading || !repeatOrderProcessed}>
                  <div />
                </Spinner>
              )
            )}
            {!isEmarket && current === CartStep.CART && (
              <StyledWholesalePaymentBlock gap={5} justify="end">
                <div className={'cart-summary-final-price'}>
                  <div className={'cart-summary-final-price-title'}>
                    <span className="desktop-label">{intl.formatMessage({ id: 'cart_summary.total' })}</span>
                    <span className="mobile-label">{intl.formatMessage({ id: 'cart_summary.amount_be_paid' })}</span>
                  </div>
                  <div className={'cart-summary-price'}>
                    {Math.max(priceTotal, 0).toFixed(2)} €
                  </div>
                </div>
                {current === CartStep.CART && <WholesaleDelivery />}
                <Tooltip
                  title={
                    !wholesaleDeliveryMode || !wholesaleDeliveries?.length
                      ? intl.formatMessage({
                          id: 'cart_summary.delivery_required',
                        })
                      : ''
                  }
                >
                  <span>
                    <Button
                      onClick={() => handleBuyProducts('PRE_PAID')}
                      className={'cart-summary-submit-btn'}
                      label={intl.formatMessage({
                        id: 'cart_summary.pre_paid',
                      })}
                      loading={isBuying}
                      size="large"
                      disabled={
                        !priceTotal ||
                        !wholesaleDeliveryMode ||
                        !wholesaleDeliveries?.length
                      }
                    />
                  </span>
                </Tooltip>
              </StyledWholesalePaymentBlock>
            )}
          </div>
        </StyledCartPage>
        <SmartIdModal
          open={isOpenSmartId}
          setIsOpen={setIsOpenSmartId}
          fingerPrint={fingerPrint}
        />
      </DefaultLayout.PageContent>
    </DefaultLayout.PageLayout>
  );
};
